/**
 * useCategoriesQuery Hook
 * 
 * React Query-based hook for loading all categories with smart caching.
 * Replaces direct API calls with a cached layer that supports:
 * - Automatic background refetching
 * - Stale-while-revalidate pattern
 * - Optimistic updates
 * - Cache invalidation
 * 
 * @example
 * ```tsx
 * const { data: categories, isLoading, error, refetch } = useCategoriesQuery();
 * 
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage message={error.message} />;
 * return <CategoryList categories={categories} />;
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CategoryService } from '@/lib/domains/category';
import { useExpenses } from '@/contexts/expense-context';
import { handleError } from '@/lib/utils/error-handler';
import { CATEGORY_CONFIG } from '@/lib/constants';

// Query keys for React Query
export const CATEGORY_QUERY_KEYS = {
    all: ['categories'] as const,
    list: (userId: string) => ['categories', 'list', userId] as const,
    detail: (categoryId: string) => ['categories', 'detail', categoryId] as const,
};

/**
 * Fetch all categories for the current user with React Query caching
 * 
 * Features:
 * - Cached for 5 minutes (staleTime)
 * - Automatically refetches on mount if stale
 * - Keeps old data while fetching new data (stale-while-revalidate)
 * - Shared cache across all components using this hook
 * 
 * @param theme - Theme for category colors/icons (defaults to 'light')
 * @returns React Query result with categories array, loading state, error, refetch function
 */
export function useCategoriesQuery(theme: 'light' | 'dark' = CATEGORY_CONFIG.DEFAULT_THEME) {
    const { currentUser } = useExpenses();

    return useQuery({
        queryKey: CATEGORY_QUERY_KEYS.list(currentUser?.id || ''),
        queryFn: async () => {
            if (!currentUser) {
                throw new Error('User not authenticated');
            }

            return CategoryService.getAllCategoriesWithPresentation(currentUser.id, theme);
        },
        enabled: !!currentUser, // Only run query if user is logged in
        staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
        gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    });
}

/**
 * Fetch a single category by ID with React Query caching
 * 
 * @param categoryId - Category ID to fetch
 * @param theme - Theme for category colors/icons
 * @returns React Query result with category data
 */
export function useCategoryQuery(
    categoryId: string | undefined,
    theme: 'light' | 'dark' = CATEGORY_CONFIG.DEFAULT_THEME
) {
    const { currentUser } = useExpenses();

    return useQuery({
        queryKey: CATEGORY_QUERY_KEYS.detail(categoryId || ''),
        queryFn: async () => {
            if (!currentUser || !categoryId) {
                throw new Error('Missing user or category ID');
            }

            return CategoryService.getCategoryById(categoryId, currentUser.id, theme);
        },
        enabled: !!currentUser && !!categoryId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
}

/**
 * Create a new category with optimistic updates
 * 
 * Automatically invalidates the categories list cache after successful creation.
 * 
 * @returns Mutation object with mutate function, loading state, error
 */
export function useCreateCategoryMutation() {
    const { currentUser } = useExpenses();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { name: string; categoryType?: string }) => {
            if (!currentUser) {
                throw new Error('User not authenticated');
            }

            return CategoryService.createCategory(currentUser.id, {
                customName: data.name,
                categoryType: data.categoryType || 'custom',
            });
        },
        onSuccess: () => {
            // Invalidate and refetch categories list
            queryClient.invalidateQueries({
                queryKey: CATEGORY_QUERY_KEYS.all,
            });
        },
        onError: (error) => {
            handleError(error, 'Failed to create category');
        },
    });
}

/**
 * Update an existing category with optimistic updates
 * 
 * Automatically invalidates the category cache after successful update.
 * 
 * @returns Mutation object with mutate function
 */
export function useUpdateCategoryMutation() {
    const { currentUser } = useExpenses();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { id: string; name?: string; icon?: string; color?: string }) => {
            if (!currentUser) {
                throw new Error('User not authenticated');
            }

            return CategoryService.updateCategory(data.id, currentUser.id, {
                customName: data.name,
            });
        },
        onSuccess: (_, variables) => {
            // Invalidate both the list and the specific category
            queryClient.invalidateQueries({
                queryKey: CATEGORY_QUERY_KEYS.all,
            });
            queryClient.invalidateQueries({
                queryKey: CATEGORY_QUERY_KEYS.detail(variables.id),
            });
        },
        onError: (error) => {
            handleError(error, 'Failed to update category');
        },
    });
}

/**
 * Delete a category with cache invalidation
 * 
 * Automatically invalidates the categories list cache after successful deletion.
 * 
 * @returns Mutation object with mutate function
 */
export function useDeleteCategoryMutation() {
    const { currentUser } = useExpenses();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (categoryId: string) => {
            if (!currentUser) {
                throw new Error('User not authenticated');
            }

            return CategoryService.deleteCategory(categoryId, currentUser.id);
        },
        onSuccess: () => {
            // Invalidate categories list
            queryClient.invalidateQueries({
                queryKey: CATEGORY_QUERY_KEYS.all,
            });
        },
        onError: (error) => {
            handleError(error, 'Failed to delete category');
        },
    });
}

/**
 * Hook to manually invalidate category cache
 * 
 * Useful when you want to force a refetch of categories from the server.
 * 
 * @example
 * ```tsx
 * const invalidateCategories = useInvalidateCategories();
 * 
 * // Force refetch
 * invalidateCategories();
 * ```
 */
export function useInvalidateCategories() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({
            queryKey: CATEGORY_QUERY_KEYS.all,
        });
    };
}
